import type { SavedItem, SaveItemImage, SearchHistoryItem } from '@/types';
import { db, type AppDatabase } from './db';

function requireOwner(ownerId: string): void {
    if (!ownerId.trim()) throw new Error('An authenticated account is required.');
}

const isUnassigned = (ownerId: string | undefined) => !ownerId?.trim();

/** One transaction makes claiming legacy data idempotent, including across tabs. */
export async function claimUnassignedData(ownerId: string, database: AppDatabase = db): Promise<void> {
    requireOwner(ownerId);
    await database.transaction('rw', database.savedItems, database.savedItemImages, database.searchHistory, async () => {
        await database.savedItems.filter((item) => isUnassigned(item.ownerId)).modify({ ownerId });
        const owners = new Map((await database.savedItems.toArray()).map((item) => [item.id, item.ownerId]));
        await database.savedItemImages.filter((image) => isUnassigned(image.ownerId)).modify((image) => {
            image.ownerId = owners.get(image.itemId) ?? ownerId;
        });
        await database.searchHistory.filter((item) => isUnassigned(item.ownerId)).modify({ ownerId });
    });
}

export async function clearLegacyAccountCache(database: AppDatabase = db): Promise<void> {
    try { globalThis.localStorage?.removeItem('pqUser'); } catch { /* No cache is used for authorization. */ }
    await database.userSettings.where('name').equals('user').modify({ value: null });
}

async function requireOwnedItem(ownerId: string, id: number, database: AppDatabase): Promise<SavedItem> {
    requireOwner(ownerId);
    const item = await database.savedItems.get(id);
    if (!item || item.ownerId !== ownerId) throw new Error('This saved item is not available to this account.');
    return item;
}

export async function getAccountItemImages(ownerId: string, itemId: number, database: AppDatabase = db): Promise<SaveItemImage[]> {
    return database.transaction('r', database.savedItems, database.savedItemImages, async () => {
        await requireOwnedItem(ownerId, itemId, database);
        return database.savedItemImages.where('[ownerId+itemId]').equals([ownerId, itemId]).toArray();
    });
}

export async function saveAccountItem(
    ownerId: string,
    itemId: number | null,
    item: Omit<SavedItem, 'id' | 'ownerId' | 'hasImages'>,
    images: ReadonlyArray<Pick<SaveItemImage, 'imgSrc'>>,
    database: AppDatabase = db,
): Promise<number> {
    requireOwner(ownerId);
    return database.transaction('rw', database.savedItems, database.savedItemImages, async () => {
        if (itemId !== null) await requireOwnedItem(ownerId, itemId, database);
        const data = { ...item, ownerId, hasImages: images.length > 0 };
        const id = itemId ?? await database.savedItems.add(data);
        if (itemId !== null) await database.savedItems.update(id, data);
        await database.savedItemImages.where('[ownerId+itemId]').equals([ownerId, id]).delete();
        await database.savedItemImages.bulkAdd(images.map(({ imgSrc }) => ({ ownerId, itemId: id, imgSrc })));
        return id;
    });
}

export async function deleteAccountItem(ownerId: string, itemId: number, database: AppDatabase = db): Promise<void> {
    await database.transaction('rw', database.savedItems, database.savedItemImages, async () => {
        await requireOwnedItem(ownerId, itemId, database);
        await database.savedItemImages.where('[ownerId+itemId]').equals([ownerId, itemId]).delete();
        await database.savedItems.delete(itemId);
    });
}

export async function setAccountItemVisibility(ownerId: string, itemId: number, visible: boolean, database: AppDatabase = db): Promise<void> {
    await database.transaction('rw', database.savedItems, async () => {
        await requireOwnedItem(ownerId, itemId, database);
        await database.savedItems.update(itemId, { visible });
    });
}

export async function setAllAccountItemsVisibility(ownerId: string, visible: boolean, database: AppDatabase = db): Promise<void> {
    requireOwner(ownerId);
    await database.savedItems.where('ownerId').equals(ownerId).modify({ visible });
}

export async function upsertSearchHistory(ownerId: string, entry: Pick<SearchHistoryItem, 'search' | 'county' | 'mode'>, database: AppDatabase = db): Promise<void> {
    requireOwner(ownerId);
    await database.transaction('rw', database.searchHistory, async () => {
        const existing = await database.searchHistory.where('[ownerId+search+county+mode]')
            .equals([ownerId, entry.search, entry.county, entry.mode]).first();
        const data = { ...entry, ownerId, createdAt: new Date() };
        if (existing) await database.searchHistory.update(existing.id, data);
        else await database.searchHistory.add(data);
    });
}
